import { Component, type ErrorInfo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';

type Props = { children: ReactNode; label?: string };
type State = { error: Error | null; info: ErrorInfo | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ error, info });
    // Extra sichtbar in Browser-Console
    console.error('[ErrorBoundary]', this.props.label ?? 'unknown', error, info);
  }

  render(): ReactNode {
    const { error, info } = this.state;
    if (!error) return this.props.children;
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
          <AlertTitle>Rendering-Fehler{this.props.label ? ` in „${this.props.label}"` : ''}</AlertTitle>
          <Box sx={{ fontFamily: 'monospace', fontSize: 12, mt: 1, whiteSpace: 'pre-wrap' }}>
            <b>{error.name}</b>: {error.message}
          </Box>
          {error.stack && (
            <Box
              component="pre"
              sx={{
                mt: 1.5,
                p: 1,
                bgcolor: 'action.hover',
                fontSize: 11,
                borderRadius: 1,
                overflowX: 'auto',
                maxHeight: 240,
              }}
            >
              {error.stack}
            </Box>
          )}
          {info?.componentStack && (
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 1,
                bgcolor: 'action.hover',
                fontSize: 11,
                borderRadius: 1,
                overflowX: 'auto',
                maxHeight: 200,
              }}
            >
              {info.componentStack}
            </Box>
          )}
          <Button
            size="small"
            sx={{ mt: 1.5 }}
            onClick={() => this.setState({ error: null, info: null })}
          >
            erneut versuchen
          </Button>
        </Alert>
      </Box>
    );
  }
}
